/**
 * LLM Streaming Types
 *
 * Normalized streaming event types shared between backend providers
 * and frontend SSE consumer. Provider-agnostic — each provider's
 * callAPIStream() maps its native SSE format to these events.
 */

import type { ProviderRoutingMetadata } from './observability/types';

/**
 * Token usage tracking (duplicated from backend llm/types.ts to avoid
 * circular dependency — shared package must not import from backend)
 */
/** Provider whose container/code-execution counters populated a usage block. */
export type ContainerProvider = 'anthropic' | 'openai';

export interface StreamTokenUsage {
  input_tokens: number;
  output_tokens: number;
  /**
   * Does NOT include cache tokens — the conversation-level analytics total
   * (`data.system.tokens_total`) adds cache_read + cache_creation on top.
   * See `compute-conversation-metrics.ts`. Don't substitute one for the other.
   */
  total_tokens: number;
  /** Input tokens served from prompt cache (cache hit). */
  cache_read_input_tokens?: number;
  /** Input tokens written to prompt cache (Anthropic only — billed higher than reads). */
  cache_creation_input_tokens?: number;
  /**
   * The 1h-TTL portion of `cache_creation_input_tokens` (Anthropic), which bills
   * at 2× input against 1.25× for 5m. Only this bucket is carried: the aggregate
   * is the sum of the per-TTL buckets, so the 5m portion is the remainder and
   * pricing derives it. Absent or 0 therefore means "no 1h writes", never
   * "unknown" — the whole aggregate then prices at the 5m rate.
   */
  cache_creation_1h_input_tokens?: number;
  /** Anthropic: `usage.server_tool_use.code_execution_requests` from this response. Omitted when web_search/web_fetch is in the same request (code execution is free in that case per Anthropic pricing). */
  code_execution_requests?: number;
  /** OpenAI: count of NEW container lifecycles observed in this response (ids not present in `provider_session.openai`). */
  container_sessions?: number;
  /** Provider whose container counters are populated above. */
  container_provider?: ContainerProvider;
}

/**
 * Normalize provider-reported input_tokens to the Anthropic convention
 * ("regular billable input only", excluding cached portion).
 *
 * OpenAI / Gemini / OpenRouter all return prompt_tokens that *include* cached
 * tokens; Anthropic returns input_tokens that *exclude* both cache reads and
 * cache writes. Subtract so cost math (cache_read priced separately) doesn't
 * double-count. Returns 0 if the subtraction would go negative.
 */
export function normalizeBillableInputTokens(promptTotal: number, cacheRead: number): number {
  return Math.max(0, (promptTotal || 0) - (cacheRead || 0));
}

/**
 * Normalized stream event types emitted by provider callAPIStream()
 * and forwarded to the frontend via SSE.
 *
 * Event lifecycle for a typical completion:
 *   message_start → content_delta* → message_end → stream_end
 *
 * With tool calls:
 *   message_start → content_delta* → tool_call_start → tool_call_delta* → tool_call_end
 *   → tool_executing → tool_result → content_delta* → message_end → stream_end
 *
 * With immediate reinvoke (agent transfer):
 *   message_start → content_delta* → tool_call_start → ... → stream_discard
 *
 * With deliver_preamble (gh #3165) — pre-tool text delivered mid-turn as its own
 * persisted message; preamble_end closes the current client bubble (non-terminal):
 *   message_start → content_delta* (preamble) → message_end → preamble_end
 *   → tool_executing → tool_result → message_start → content_delta* → message_end → stream_end
 */
export type StreamEvent =
  /** Text content chunk from the LLM */
  | { type: 'content_delta'; delta: string }
  /** Tool call started — contains tool ID and name */
  | { type: 'tool_call_start'; toolCall: { id: string; name: string } }
  /** Partial JSON argument fragment for a tool call */
  | { type: 'tool_call_delta'; toolCallId: string; argsDelta: string }
  /** Tool call arguments fully received */
  | { type: 'tool_call_end'; toolCallId: string }
  /** Tool is being executed server-side (informational, for UI indicators) */
  | { type: 'tool_executing'; toolName: string; toolCallId: string }
  /** Tool execution completed (informational) */
  | { type: 'tool_result'; toolName: string; toolCallId: string }
  /** Token usage from the provider (emitted at end of each LLM turn) */
  | { type: 'usage'; usage: StreamTokenUsage }
  /** LLM response started */
  | { type: 'message_start'; messageId: string }
  /** LLM response ended (one turn — may be followed by tool execution + new turn) */
  | { type: 'message_end'; stopReason: string }
  /** Provider session data (e.g., Anthropic container_id for reuse) */
  | { type: 'session'; session: Record<string, unknown> }
  /** Sanitized provider-router decision emitted when a gateway exposes it. */
  | { type: 'provider_routing'; routing: ProviderRoutingMetadata }
  /** Immediate reinvoke — discard all accumulated text (agent transfer) */
  | { type: 'stream_discard' }
  /** A pre-tool preamble was persisted + delivered mid-turn (deliver_preamble,
   *  gh #3165). NON-terminal: the client finalizes the current bubble with this
   *  DB messageId/content and opens a new one for the continuation. Emitted by
   *  the ConversationDO delivery seam (not by providers). Old clients ignore it. */
  | { type: 'preamble_end'; messageId: string; content: string }
  /** Stream fully complete — contains final message ID and full content */
  | { type: 'stream_end'; messageId: string; content: string }
  /** Internal: consolidated reasoning/thinking content blocks emitted once at end of stream.
   *  Used to round-trip provider-specific reasoning blocks (Anthropic thinking, OpenRouter
   *  reasoning_details) across tool-loop iterations. Not forwarded to client.
   *  Typed `readonly` to enforce the "unmodified round-trip" contract at compile time.
   *  `progressUpdate`: user-facing progress text some models write before a tool call
   *  (Anthropic progress-update thinking blocks) — see ProviderResponse.progressUpdate. */
  | { type: 'reasoning_blocks'; blocks: readonly unknown[]; progressUpdate?: string }
  /** Error during streaming */
  | { type: 'error'; error: string }
  /** Turn was rate-limited and rescheduled via the DO alarm. Client should expect
   *  the response to arrive on the next live-update push, not on this stream. */
  | { type: 'rescheduled'; retry_after_ms: number };
